import React, { useState } from 'react';
import Layout from '../components/Layout';
import { Rocket, Clock, Eye, GitBranch, Repeat, Check, X, ArrowRight } from 'lucide-react';
import { PrereqChecklist } from '../components/PrereqChecklist';
import SetupJourney from '../guided/SetupJourney';
import { AgencyReplacementMap } from '../components/AgencyReplacementMap';
import { TrackingCodeGuide } from '../components/TrackingCodeGuide';
import { registerTrainingInterest } from '../utils/api';
import { Link } from 'react-router-dom';

const TABS = [
  { id: 'why', label: 'Why This Platform' },
  { id: 'prereqs', label: 'Before You Start' },
  { id: 'agency', label: 'Run Ads Like Your Own Agency' },
  { id: 'tracking', label: 'Tracking & Your Numbers' },
  { id: 'week', label: 'Your First Week' },
  { id: 'training', label: 'Done-With-You Training' },
];

const LessonTag = ({ tag, framing }) => (
  <div className="mb-6" data-testid="lesson-framing">
    <span className="inline-block px-2.5 py-1 rounded-full bg-lime/10 border border-lime/40 text-lime text-[13px] font-black uppercase tracking-wider mb-2">{tag}</span>
    <p className="text-lg text-light-gray italic">{framing}</p>
  </div>
);

const NextStep = ({ onClick, to, children }) => {
  const cls = 'inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-lime text-black font-black text-sm hover:bg-lime/90 transition-colors mt-2';
  return to ? (
    <Link to={to} className={cls} data-testid="next-step-btn">{children} <ArrowRight size={16} /></Link>
  ) : (
    <button onClick={onClick} className={cls} data-testid="next-step-btn">{children} <ArrowRight size={16} /></button>
  );
};

const CHANNELS = [
  { name: 'Google Ads', body: 'Catches people already searching "AC repair near me."' },
  { name: 'Facebook & Instagram', body: 'Gets you seen by neighbors before they search.' },
  { name: 'Your Google listing + print', body: 'Win the map results and your own streets.' },
  { name: 'Past customers (email & text)', body: 'They already trust you. Cheapest jobs you\u2019ll ever book.' },
  { name: 'Reviews & referrals', body: 'Ask after every job. One happy customer brings two.' },
];

const LOOP = [
  {
    icon: Rocket, name: 'LAUNCH', color: 'text-lime',
    body: 'Open a campaign, follow the numbered steps, use the pre-written copy → live in an afternoon.',
  },
  {
    icon: Clock, name: 'WAIT', color: 'text-orange',
    body: "Hands off for 3–7 days → the platforms' AI learns who calls. Every edit resets the learning.",
  },
  {
    icon: Eye, name: 'CHECK', color: 'text-lime',
    body: 'Look at ONE number: cost-per-call. $25–$50 is normal. Likes and impressions don\u2019t pay bills — ignore them.',
  },
  {
    icon: GitBranch, name: 'DECIDE', color: 'text-orange',
    body: 'Too expensive for 3+ days → change ONE thing. In range → leave it alone. No calls all week → check your phone setup.',
  },
  {
    icon: Repeat, name: 'REPEAT', color: 'text-lime',
    body: 'Run this loop once a week → that\u2019s the job agencies charge thousands a month for.',
  },
];

const WEEK = [
  { day: 'Day 1', title: 'Set up your foundations', body: 'Fill in My Brand + the Before-You-Start checklist → your ads look professional and can go live.' },
  { day: 'Day 2', title: 'Launch your first ad', body: 'Pick one campaign, publish the Facebook ad → calls typically start in 3–5 days.' },
  { day: 'Day 3', title: 'Add the Google ad', body: 'Publish the Google Search ad → now you catch the scrollers AND the searchers.' },
  { day: 'Day 4', title: 'Free stuff day', body: 'Post the social post + email past customers → costs nothing, often books the first jobs.' },
  { day: 'Day 5', title: 'Do nothing to the ads', body: 'The AI is still learning. Answer every call fast → book more of the leads you\u2019re paying for.' },
  { day: 'Day 6–7', title: 'First check-in', body: 'Check cost-per-call. $25–$50 → keep going. Too high → change ONE thing. Log your booked jobs.' },
];

export default function StartHere() {
  const [tab, setTab] = useState('why');
  const [interested, setInterested] = useState(() => localStorage.getItem('training_interest') === '1');

  const handleInterest = async () => {
    setInterested(true);
    localStorage.setItem('training_interest', '1');
    try { await registerTrainingInterest(); } catch (e) { /* recorded locally */ }
  };

  return (
    <Layout variant="light">
      <div data-testid="start-here-page" className="max-w-4xl">
        <h1 className="text-4xl font-black text-white mb-2">Start Here</h1>
        <p className="text-light-gray mb-8">Work through the setup below → then learn the skill that replaces the retainer.</p>

        <div className="mb-12" data-testid="start-here-journey">
          <SetupJourney />
        </div>

        <h2 className="text-3xl font-black text-white mb-2">Training & Playbook</h2>
        <p className="text-light-gray mb-4">Learn this once → stop paying agency retainers forever.</p>
        <Link to="/playbooks" data-testid="start-here-playbooks-banner" className="flex items-center justify-between bg-orange/10 border border-orange/40 rounded-xl px-5 py-3.5 mb-8 hover:bg-orange/20 transition-colors">
          <span className="text-sm text-light-gray">Ready to launch a channel? Step-by-step guides live in <span className="text-orange font-bold">Playbooks</span>.</span>
          <span className="text-orange font-bold text-sm shrink-0 ml-4">Open Playbooks →</span>
        </Link>

        <div className="flex flex-wrap gap-1 mb-8 border-b border-divider">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`start-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${
                tab === t.id ? 'border-lime text-lime' : 'border-transparent text-gray hover:text-white'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'why' && (
          <div className="space-y-6" data-testid="start-why-section">
            <LessonTag tag="Lesson 1 of 6 · What marketing actually is"
              framing="Make the phone ring → book the jobs → know what each job cost you. That's the whole skill." />
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-card-bg border border-[#ec6a1c]/25 rounded-xl p-5">
                <h3 className="text-[#ec6a1c] font-black text-2xl md:text-3xl uppercase leading-tight mb-3">The agency way</h3>
                <ul className="space-y-2.5 text-sm text-light-gray">
                  <li className="flex gap-2"><X size={15} className="text-[#ec6a1c] shrink-0 mt-0.5" /> $5,000–$20,000+ a month, forever</li>
                  <li className="flex gap-2"><X size={15} className="text-[#ec6a1c] shrink-0 mt-0.5" /> Contracts that lock you in when results dry up</li>
                  <li className="flex gap-2"><X size={15} className="text-[#ec6a1c] shrink-0 mt-0.5" /> They own your ad accounts and data — cancel and it's gone</li>
                  <li className="flex gap-2"><X size={15} className="text-[#ec6a1c] shrink-0 mt-0.5" /> You never learn how any of it works</li>
                </ul>
              </div>
              <div className="bg-card-bg border border-lime/25 rounded-xl p-5">
                <h3 className="text-lime font-black text-2xl md:text-3xl uppercase leading-tight mb-3">This platform</h3>
                <ul className="space-y-2.5 text-sm text-light-gray">
                  <li className="flex gap-2"><Check size={15} className="text-lime shrink-0 mt-0.5" /> A fraction of one month's retainer — cancel anytime</li>
                  <li className="flex gap-2"><Check size={15} className="text-lime shrink-0 mt-0.5" /> You own your ad accounts, website, and data</li>
                  <li className="flex gap-2"><Check size={15} className="text-lime shrink-0 mt-0.5" /> Pre-written campaigns + AI drafts → approve and publish</li>
                  <li className="flex gap-2"><Check size={15} className="text-lime shrink-0 mt-0.5" /> A skill your business keeps forever</li>
                </ul>
              </div>
            </div>

            <AgencyReplacementMap />

            <div className="bg-black border border-orange/40 rounded-xl p-5">
              <h3 className="text-orange font-bold text-xl mb-2">The Real Tradeoff</h3>
              <p className="text-light-gray text-sm leading-relaxed">
                A few hours a week from you → thousands a month saved in agency fees. If that trade doesn't fit your stage, we'll tell you upfront.
              </p>
            </div>
            <NextStep onClick={() => setTab('prereqs')}>Next: Before You Start</NextStep>
          </div>
        )}

        {tab === 'prereqs' && (
          <div data-testid="start-prereqs-section">
            <LessonTag tag="The foundations"
              framing="Do this setup once → every campaign in the vault becomes plug-and-play." />
            <h2 className="text-2xl font-bold text-white mb-2">Before you start</h2>
            <p className="text-gray text-sm mb-5">Check these off → every campaign links back here, ready to fire.</p>
            <PrereqChecklist />
            <NextStep onClick={() => setTab('agency')}>Next: Run Ads Like Your Own Agency</NextStep>
          </div>
        )}

        {tab === 'agency' && (
          <div className="space-y-8" data-testid="start-agency-section">
            <LessonTag tag="Lessons 2 & 3 of 6 · How leads get generated + how to run a campaign"
              framing="Learn where leads come from → run the weekly loop → skip the retainer." />
            <div data-testid="lead-channels-section">
              <h2 className="text-2xl font-bold text-white mb-2">Where leads actually come from</h2>
              <p className="text-light-gray text-sm mb-4">There are only five ways a homeowner finds you:</p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {CHANNELS.map((c) => (
                  <div key={c.name} className="bg-black border border-divider rounded-xl p-4">
                    <p className="text-lime font-bold text-sm mb-1">{c.name}</p>
                    <p className="text-xs text-light-gray leading-relaxed">{c.body}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">The 5-step loop that IS your agency</h2>
              <p className="text-light-gray text-sm mb-6">Agencies run this exact loop every week. Now you will too.</p>
              <div className="space-y-3">
                {LOOP.map((s, i) => (
                  <div key={s.name} className="bg-card-bg border border-divider rounded-xl p-5 flex gap-4">
                    <div className="shrink-0 flex flex-col items-center">
                      <s.icon size={22} className={s.color} />
                      {i < LOOP.length - 1 && <div className="w-px flex-1 bg-divider mt-2" />}
                    </div>
                    <div>
                      <h3 className={`font-black text-xl tracking-wide ${s.color}`}>{s.name}</h3>
                      <p className="text-light-gray text-sm mt-1 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-2">How ad accounts are organized (so you know which layer to touch)</h3>
              <p className="text-gray text-sm mb-4">Both Meta and Google use the same 3-layer structure:</p>
              <div className="grid md:grid-cols-3 gap-3">
                <div className="bg-black border border-divider rounded-xl p-4">
                  <p className="text-lime font-bold text-lg mb-1">1. Campaign</p>
                  <p className="text-xs text-gray-light">THE GOAL. "Get calls." Set once, rarely touch.</p>
                </div>
                <div className="bg-black border border-divider rounded-xl p-4">
                  <p className="text-orange font-bold text-lg mb-1">2. Ad Set <span className="text-gray font-normal">(Google: Ad Group)</span></p>
                  <p className="text-xs text-gray-light">WHO SEES IT + BUDGET. Touch to change budget or area.</p>
                </div>
                <div className="bg-black border border-divider rounded-xl p-4">
                  <p className="text-lime font-bold text-lg mb-1">3. Ad</p>
                  <p className="text-xs text-gray-light">WHAT THEY SEE. Change this when cost-per-call is too high.</p>
                </div>
              </div>
              <p className="text-sm text-light-gray mt-4 bg-card-bg border border-divider rounded-lg p-4">
                💡 <span className="font-semibold text-white">Rule of thumb:</span> too expensive → change the <span className="text-lime font-semibold">Ad</span>. Wrong area/budget → change the <span className="text-orange font-semibold">Ad Set</span>. Leave the Campaign layer alone — the automated settings win.
              </p>
            </div>
            <NextStep onClick={() => setTab('tracking')}>Next: Tracking & Your Numbers</NextStep>
          </div>
        )}

        {tab === 'tracking' && (
          <div data-testid="start-tracking-section">
            <LessonTag tag="Lessons 4 & 5 of 6 · Tracking + reading your numbers"
              framing="Set up tracking → every lead gets credited, and you know which ad pays for itself." />
            <TrackingCodeGuide />
            <NextStep onClick={() => setTab('week')}>Next: Your First Week</NextStep>
          </div>
        )}

        {tab === 'week' && (
          <div data-testid="start-week-section">
            <LessonTag tag="Lesson 6 of 6 · Make it a habit"
              framing="Follow this for 7 days → your first campaign live, measured, and booking jobs." />
            <h2 className="text-2xl font-bold text-white mb-6">Your first week, day by day</h2>
            <div className="space-y-3">
              {WEEK.map((d) => (
                <div key={d.day} className="bg-card-bg border border-divider rounded-xl p-5 flex gap-4">
                  <span className="shrink-0 w-20 text-lime font-black text-sm pt-0.5">{d.day}</span>
                  <div>
                    <h3 className="text-white font-bold text-lg">{d.title}</h3>
                    <p className="text-light-gray text-sm mt-1 leading-relaxed">{d.body}</p>
                  </div>
                </div>
              ))}
            </div>
            <NextStep to="/vault">Start now: pick your first campaign</NextStep>
          </div>
        )}

        {tab === 'training' && (
          <div data-testid="start-training-section" className="max-w-2xl">
            <h2 className="text-2xl font-bold text-white mb-3">Too busy to learn this yourself?</h2>
            <p className="text-light-gray text-sm leading-relaxed mb-5">
              <span className="text-white font-semibold">Platform + Training:</span> we train someone on your team — office manager, spouse, sharpest tech — until they run this whole system confidently.
            </p>
            {interested ? (
              <p className="text-lime font-semibold text-sm" data-testid="training-interest-confirmed">✓ You're on the list — we'll reach out when it opens.</p>
            ) : (
              <button
                data-testid="training-interest-btn"
                onClick={handleInterest}
                className="px-6 py-3 rounded-lg bg-orange text-white font-bold hover:bg-orange/90"
              >
                I'm interested — put me on the waitlist
              </button>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
